import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core'
import useSWR from 'swr'
import type { SWRConfiguration, SWRResponse } from 'swr'
import useSWRMutation from 'swr/mutation'
import type { SWRMutationConfiguration } from 'swr/mutation'
import { GRAPHQL_HMAC_HEADER } from '~/graphql/header'
import { SINGLE_DOCUMENT_HMAC } from '~/lib/graphql/hmac'
import { GRAPHQL_API_ROUTES_PATH } from '~/lib/path/path'

export type RequestParam<T, V> = {
  query: DocumentTypeDecoration<T, V>
  variables: V
  headers?: HeadersInit
}

export async function fetcher<T, V>({
  query,
  variables,
  headers: _headers,
}: RequestParam<T, V>): Promise<T> {
  const headers = new Headers(_headers)
  headers.set('Content-Type', 'application/json')
  const queryHmac = SINGLE_DOCUMENT_HMAC[query.toString()]
  if (queryHmac !== undefined) {
    headers.set(GRAPHQL_HMAC_HEADER, queryHmac)
  }
  const response = await fetch(GRAPHQL_API_ROUTES_PATH, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query,
      variables,
    }),
  })
  if (!response.ok) {
    throw new Error(`status=${response.status} document=${query.toString()}`)
  }
  const json = await response.json()
  // https://spec.graphql.org/October2021/#sel-EAPHFCAACC0EnvC
  // If the request raised any errors, the response map must contain an entry with key errors.
  // If the request completed without raising any errors, this entry must not be present.
  // なので errors がなければエラーがないと見なおしてよい
  if (json['errors'] !== undefined) {
    throw new Error(`GraphQL error: ${JSON.stringify(json.errors)}`)
  }
  return json.data as T
}

type Options = {
  headers?: RequestInit['headers']
  fetch?: boolean
  config?: SWRConfiguration
}

// graphql-request/dist/types.d.ts からぱくってきてます
type RemoveIndex<T> = {
  [K in keyof T as string extends K ? never : number extends K ? never : K]: T[K]
}
type VariablesAndOptions<V> = V extends Record<any, never>
  ? [variables?: V, options?: Options]
  : keyof RemoveIndex<V> extends never
    ? [variables?: V, options?: Options]
    : [variables: V, options?: Options]

/** @deprecated use atomWithQuery */
export function useSwrGraphqlRequest<T, V>(
  query: DocumentTypeDecoration<T, V>,
  ...variablesAndOptions: VariablesAndOptions<V>
): SWRResponse<T, Error> {
  const [variables, options] = variablesAndOptions
  const { fetch = true, headers, config } = options ?? {}
  const defaultConfig = { ...config, revalidateOnFocus: config?.revalidateOnFocus ?? false }
  return useSWR<T, Error>(fetch ? { query, variables, headers } : null, fetcher, defaultConfig)
}

export function useSwrGraphqlMutation<T, V>(
  query: DocumentTypeDecoration<T, V>,
  variables: V,
  options?: SWRMutationConfiguration<
    T,
    Error,
    { query: DocumentTypeDecoration<T, V>; variables: V }
  >
) {
  return useSWRMutation<T, Error, { query: DocumentTypeDecoration<T, V>; variables: V }>(
    { query, variables },
    fetcher,
    // @ts-ignore 型エラー解決できず...
    options
  )
}
